import {
  ArrowLeftRight,
  BadgePercent,
  BarChart3,
  Bell,
  Bug,
  Calendar,
  CalendarClock,
  ClipboardList,
  Cog,
  Cpu,
  CreditCard,
  DollarSign,
  DoorOpen,
  Flag,
  FlaskConical,
  FormInput,
  GraduationCap,
  Hash,
  History,
  KeyIcon,
  Layers,
  Lightbulb,
  Link as LinkIcon,
  Mail,
  Mails,
  Megaphone,
  PartyPopper,
  Plug,
  Plus,
  Receipt,
  RefreshCw,
  ScrollText,
  Send,
  Settings2,
  ShieldCheck,
  Smartphone,
  Tags,
  Theater,
  UploadCloud,
  UserRoundPlus,
  Users,
  Zap,
} from 'lucide-react';
import { useMemo } from 'react';

import { useTriggerExpiredEvents } from '~entities/stripe/api/trigger-expired-events';
import { useTriggerMembershipFeeReminder } from '~entities/stripe/api/trigger-membership-fee-reminder';
import { useTriggerPaymentStateSync } from '~entities/stripe/api/trigger-payment-state-sync';
import { useTriggerScheduledDrops } from '~entities/stripe/api/trigger-scheduled-drops';
import { useTriggerScheduledInvoices } from '~entities/stripe/api/trigger-scheduled-invoices';
import { useTriggerUpcomingPayments } from '~entities/stripe/api/trigger-upcoming-payments';
import { type StudioDetails } from '~entities/studios/model/types';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';
import { useCurrentUser } from '~entities/users/model/user-store';

import { isEnrollioFormsNavEnabled } from '~shared/utils/is-enrollio-forms-nav-enabled';
import { isEnrollioStaffEmail } from '~shared/utils/is-enrollio-staff-email';
import { isDevOptionsVisible } from '~shared/utils/is-standalone-dev-environment';

import type { NavGroup } from './nav-section';

export interface DashboardNavMenu {
  label: string;
  sectionKey: string;
  /** When set with empty sections, render as a direct top-level link (e.g. Dashboard). */
  to?: string;
  widthClassName?: string;
  columns?: 1 | 2;
  align?: 'start' | 'end';
  sections: Array<NavGroup>;
}

interface UseDashboardNavMenusOptions {
  studioData?: StudioDetails;
  onAddStudent: () => void;
  onAddParent: () => void;
  copyRegistrationFormLink: () => void;
  copyStudioLoginLink: () => void;
  copyParentPortalLink: () => void;
}

/**
 * ENR-3525 menu IA:
 * Dashboard → Families → Classes & Events → Billing → Communications → Reports
 * Right: Settings → Help → account
 * No More menu.
 */
export const useDashboardNavMenus = ({
  studioData,
  onAddStudent,
  onAddParent,
  copyRegistrationFormLink,
  copyStudioLoginLink,
  copyParentPortalLink,
}: UseDashboardNavMenusOptions): Array<DashboardNavMenu> => {
  const { hasAdminAccess, hasSuperAdminAccess } = useCurrentUserRole();
  const currentUser = useCurrentUser();
  const showDevOptions = isDevOptionsVisible();
  const showFormsNav = isEnrollioFormsNavEnabled();
  const isV2 = studioData?.isV2 === true;
  const isEnrollioStaff = isEnrollioStaffEmail(currentUser?.email);
  const triggerUpcomingPaymentsMutation = useTriggerUpcomingPayments();
  const triggerScheduledInvoicesMutation = useTriggerScheduledInvoices();
  const triggerPaymentStateSyncMutation = useTriggerPaymentStateSync();
  const triggerScheduledDropsMutation = useTriggerScheduledDrops();
  const triggerMembershipFeeReminderMutation =
    useTriggerMembershipFeeReminder();
  const triggerExpiredEventsMutation = useTriggerExpiredEvents();

  return useMemo(() => {
    const menus: Array<DashboardNavMenu> = [
      {
        label: 'Dashboard',
        sectionKey: 'dashboard',
        to: '/dashboard',
        sections: [],
      },
      {
        label: 'Families',
        sectionKey: 'families',
        widthClassName: 'w-[min(340px,calc(100vw-2rem))]',
        sections: [
          {
            id: 'manage',
            label: 'Manage',
            items: [
              ...(hasAdminAccess
                ? [
                    {
                      to: '/parents',
                      label: 'All Families',
                      Icon: Users,
                    },
                  ]
                : []),
              { to: '/students', label: 'Students', Icon: GraduationCap },
              // Complete enrollment (ENR-3555) — add when feature + permission gate exist; no placeholder.
            ],
          },
          ...(hasAdminAccess
            ? [
                {
                  id: 'add',
                  label: 'Add',
                  items: [
                    {
                      label: 'Add Family',
                      Icon: UserRoundPlus,
                      onClick: onAddParent,
                    },
                    {
                      label: 'Add Student',
                      Icon: UserRoundPlus,
                      onClick: onAddStudent,
                    },
                    {
                      to: '/parents/bulk-upload',
                      label: 'Bulk Upload',
                      Icon: UploadCloud,
                    },
                  ],
                },
                {
                  id: 'registration',
                  label: 'Registration',
                  items: [
                    {
                      label: 'Share Registration Form',
                      Icon: LinkIcon,
                      description: 'Copy a public family sign-up link',
                      onClick: copyRegistrationFormLink,
                    },
                  ],
                },
              ]
            : []),
        ],
      },
      {
        label: 'Classes & Events',
        sectionKey: 'classes',
        widthClassName: 'w-80',
        sections: [
          {
            id: 'manage',
            label: 'Manage',
            items: [
              { to: '/classes', label: 'All Classes', Icon: Calendar },
              {
                to: '/class-calendar',
                label: 'Class Calendar',
                Icon: CalendarClock,
              },
              { to: '/events', label: 'All Events', Icon: PartyPopper },
              ...(hasAdminAccess
                ? [
                    {
                      to: '/recitals',
                      label: 'Recital Manager',
                      Icon: Theater,
                    },
                  ]
                : []),
            ],
          },
          {
            id: 'scheduling',
            label: 'Scheduling',
            items: [
              ...(hasAdminAccess
                ? [
                    { to: '/sessions', label: 'Sessions', Icon: Layers },
                    {
                      to: '/sessions/rollover',
                      label: 'Session Rollover Manager',
                      Icon: RefreshCw,
                    },
                  ]
                : []),
              { to: '/room-overview', label: 'Rooms', Icon: DoorOpen },
            ],
          },
          ...(hasAdminAccess
            ? [
                {
                  id: 'setup',
                  label: 'Setup',
                  items: [
                    { to: '/classes/new', label: 'Add Class', Icon: Plus },
                    {
                      to: '/classes/bulk-upload',
                      label: 'Bulk Add Classes',
                      Icon: UploadCloud,
                    },
                    {
                      to: '/category-config/groups',
                      label: 'Class Categories',
                      Icon: Tags,
                      activePaths: ['/category-config'],
                    },
                  ],
                },
              ]
            : []),
        ],
      },
    ];

    if (hasSuperAdminAccess) {
      menus.push({
        label: 'Billing',
        sectionKey: 'billing',
        columns: 2,
        widthClassName: 'w-[min(660px,calc(100vw-2rem))]',
        sections: [
          {
            id: 'billing-ops',
            label: 'Billing',
            column: 1,
            items: [
              {
                to: '/payment/payment-dashboard',
                label: 'Payment Dashboard',
                Icon: CreditCard,
              },
              {
                to: '/payment/payment-run-review',
                label: 'Payment Run Review',
                Icon: ClipboardList,
              },
              {
                to: '/payment/studio-transaction-table',
                label: 'Transactions',
                Icon: ArrowLeftRight,
                activePaths: ['/payment/studio-transaction-table'],
              },
            ],
          },
          {
            id: 'billing-setup',
            label: 'Reference data',
            column: 2,
            items: [
              { to: '/discounts', label: 'Discounts', Icon: BadgePercent },
              {
                to: '/payment/payment-integration',
                label: 'Connect Payment Provider',
                Icon: Plug,
              },
              {
                to: '/payment/payment-processing',
                label: 'Payment Processing',
                Icon: Cpu,
              },
              {
                to: '/payment/payment-settings',
                label: 'Tuition settings',
                Icon: Settings2,
              },
              {
                to: '/payment/transaction-codes',
                label: 'Transaction Codes',
                Icon: Hash,
              },
              {
                to: '/payment/transaction-fee-settings',
                label: 'Transaction Fee Rules',
                Icon: Receipt,
              },
              {
                to: '/payment/hourly-tuition',
                label: 'Hourly Tuition',
                Icon: Calendar,
              },
            ],
          },
        ],
      });
    }

    if (hasAdminAccess) {
      menus.push({
        label: 'Communications',
        sectionKey: 'communications',
        widthClassName: 'w-80',
        sections: [
          {
            id: 'communicate',
            label: 'Communicate',
            items: [
              { to: '/announcements', label: 'Announcements', Icon: Megaphone },
              {
                to: '/email-builder',
                label: 'Campaign Builder',
                Icon: Mails,
              },
              {
                to: '/outgoing-email-logs',
                label: 'Sent Emails',
                Icon: Send,
                description: 'View delivery history and status',
              },
              {
                to: '/reminders',
                label: 'Reminders',
                Icon: Bell,
                description: 'Configure automated reminders before lessons',
              },
            ],
          },
        ],
      });
    }

    if (hasSuperAdminAccess) {
      menus.push({
        label: 'Reports',
        sectionKey: 'reports',
        widthClassName: 'w-80',
        sections: [
          {
            id: 'catalog',
            label: 'Catalog',
            items: [
              {
                to: '/payment/payment-reports',
                label: 'Studio Reports',
                Icon: BarChart3,
              },
              {
                to: '/performance-dashboard',
                label: 'Studio Performance',
                Icon: BarChart3,
              },
            ],
          },
        ],
      });
    }

    if (hasAdminAccess) {
      menus.push({
        label: 'Settings',
        sectionKey: 'settings',
        columns: hasSuperAdminAccess ? 2 : 1,
        widthClassName: hasSuperAdminAccess
          ? 'w-[min(640px,calc(100vw-2rem))]'
          : 'w-[min(20rem,calc(100vw-2rem))]',
        align: 'end',
        sections: hasSuperAdminAccess
          ? [
              {
                id: 'general',
                label: 'General',
                column: 1,
                items: [
                  {
                    to: '/studio-profile',
                    label: 'Studio Profile',
                    Icon: Cog,
                  },
                  {
                    to: '/parent-form-config',
                    label: 'Registration Form',
                    Icon: ClipboardList,
                    id: 'registration-form',
                  },
                  { to: '/demo-mode', label: 'Demo Mode', Icon: FlaskConical },
                  ...(showDevOptions
                    ? [{ to: '/v2-mode', label: 'V2 Mode', Icon: Zap }]
                    : []),
                ],
              },
              {
                id: 'ops-settings',
                label: 'Operations',
                column: 1,
                items: [
                  {
                    to: '/email-settings',
                    label: 'Email Settings',
                    Icon: Mail,
                    description: 'Email delivery and notification toggles',
                  },
                  {
                    to: '/email-customizer',
                    label: 'Email Templates',
                    Icon: ScrollText,
                  },
                  {
                    to: '/makeup-classes',
                    label: 'Makeup Classes',
                    Icon: CalendarClock,
                  },
                  {
                    to: '/trial-bookings',
                    label: 'Trial Bookings',
                    Icon: Calendar,
                  },
                  {
                    to: '/mobile-inventor-integration',
                    label: 'Push Notification',
                    Icon: Smartphone,
                  },
                  {
                    to: '/daily-digest',
                    label: 'Daily Digest',
                    Icon: Mail,
                  },
                ],
              },
              {
                id: 'access',
                label: 'Access & setup',
                column: 2,
                items: [
                  {
                    to: '/drt-integration',
                    label: 'DRT Integration',
                    Icon: KeyIcon,
                  },
                  {
                    to: '/crm-email-sync',
                    label: 'CRM Email Sync',
                    Icon: RefreshCw,
                  },
                  {
                    to: '/user-management',
                    label: 'Users & Access',
                    Icon: Users,
                  },
                  { to: '/policies', label: 'Policies', Icon: ScrollText },
                  {
                    to: '/import-data',
                    label: 'Import Wizard',
                    Icon: UploadCloud,
                    activePaths: ['/import-data', '/import-data-logs'],
                  },
                  {
                    to: '/integrations',
                    label: 'Studio Currency',
                    Icon: DollarSign,
                    id: 'studio-currency',
                  },
                  {
                    to: '/audit-logs',
                    label: 'Audit Logs',
                    Icon: History,
                  },
                  ...(showFormsNav
                    ? [
                        {
                          to: '/forms' as const,
                          label: 'Forms',
                          Icon: FormInput,
                        },
                        {
                          to: '/form-submissions' as const,
                          label: 'Form Submissions',
                          Icon: ClipboardList,
                        },
                      ]
                    : []),
                  ...(isEnrollioStaff
                    ? [
                        {
                          to: '/portal-banner' as const,
                          label: 'Portal Banners',
                          Icon: Flag,
                        },
                      ]
                    : []),
                ],
              },
              {
                id: 'portal-links',
                label: 'Portal Links',
                column: 2,
                items: [
                  {
                    to: '/preflight-check',
                    label: 'Setup Checklist',
                    Icon: ShieldCheck,
                    description:
                      'Review required studio configuration and integrations',
                  },
                  {
                    id: 'copy-studio-login-link',
                    label: 'Studio Login Link',
                    Icon: LinkIcon,
                    description: 'Copy the staff sign-in URL',
                    onClick: copyStudioLoginLink,
                  },
                  {
                    id: 'copy-parent-portal-link',
                    label: 'Parent Portal Link',
                    Icon: LinkIcon,
                    description: 'Copy the parent portal URL',
                    onClick: copyParentPortalLink,
                  },
                  {
                    to: '/public-page-link',
                    label: 'Public Page Link',
                    Icon: LinkIcon,
                    description: 'Manage the public schedule page link',
                  },
                ],
              },
              ...(showDevOptions
                ? [
                    {
                      id: 'dev-crons',
                      column: 2 as const,
                      items: [
                        {
                          label: 'Run Upcoming Payments Cron',
                          Icon: RefreshCw,
                          description: isV2
                            ? 'POST /v2/cron/upcoming-payments'
                            : 'POST /cron/upcoming-payments',
                          onClick: () => {
                            triggerUpcomingPaymentsMutation.mutate({ isV2 });
                          },
                        },
                        {
                          label: 'Run Scheduled Invoices Cron',
                          Icon: RefreshCw,
                          description: isV2
                            ? 'POST /v2/cron/scheduled-invoices'
                            : 'POST /cron/scheduled-invoices',
                          onClick: () => {
                            triggerScheduledInvoicesMutation.mutate({ isV2 });
                          },
                        },
                        {
                          label: 'Run Payment State Sync Cron',
                          Icon: RefreshCw,
                          description: isV2
                            ? 'POST /v2/cron/payment-state-sync'
                            : 'POST /cron/payment-state-sync',
                          onClick: () => {
                            triggerPaymentStateSyncMutation.mutate({ isV2 });
                          },
                        },
                        {
                          label: 'Run Scheduled Drops Cron',
                          Icon: RefreshCw,
                          description: isV2
                            ? 'POST /v2/cron/scheduled-drops'
                            : 'POST /cron/scheduled-drops',
                          onClick: () => {
                            triggerScheduledDropsMutation.mutate({ isV2 });
                          },
                        },
                        {
                          label: 'Run Fees Owed Reminder Cron',
                          Icon: RefreshCw,
                          description: 'POST /cron/membership-fee-reminder',
                          onClick: () => {
                            triggerMembershipFeeReminderMutation.mutate({
                              isV2,
                            });
                          },
                        },
                        {
                          label: 'Run Expired Events Cron',
                          Icon: RefreshCw,
                          description: 'POST /cron/expired-events',
                          onClick: () => {
                            triggerExpiredEventsMutation.mutate({ isV2 });
                          },
                        },
                      ],
                    },
                  ]
                : []),
            ]
          : [
              {
                id: 'portal-links',
                label: 'Portal Links',
                items: [
                  {
                    to: '/preflight-check',
                    label: 'Setup Checklist',
                    Icon: ShieldCheck,
                    description:
                      'Review required studio configuration and integrations',
                  },
                  {
                    id: 'copy-studio-login-link',
                    label: 'Studio Login Link',
                    Icon: LinkIcon,
                    description: 'Copy the staff sign-in URL',
                    onClick: copyStudioLoginLink,
                  },
                  {
                    id: 'copy-parent-portal-link',
                    label: 'Parent Portal Link',
                    Icon: LinkIcon,
                    description: 'Copy the parent portal URL',
                    onClick: copyParentPortalLink,
                  },
                  {
                    to: '/public-page-link',
                    label: 'Public Page Link',
                    Icon: LinkIcon,
                    description: 'Manage the public schedule page link',
                  },
                ],
              },
            ],
      });
    }

    menus.push({
      label: 'Help',
      sectionKey: 'help',
      widthClassName: 'w-80',
      align: 'end',
      sections: [
        {
          id: 'help',
          label: 'Support',
          items: [
            {
              href: 'https://help.enrollio.ai/',
              label: 'Report a Bug',
              Icon: Bug,
            },
            {
              href: 'https://feedback.enrollio.ai/',
              label: 'Request a Feature',
              Icon: Lightbulb,
            },
            {
              href: 'https://enrollio.ai/studio-branded-app',
              label: 'Get Your Studio App',
              Icon: Smartphone,
            },
          ],
        },
      ],
    });

    return menus;
  }, [
    copyParentPortalLink,
    copyRegistrationFormLink,
    copyStudioLoginLink,
    hasAdminAccess,
    hasSuperAdminAccess,
    isEnrollioStaff,
    showDevOptions,
    showFormsNav,
    isV2,
    onAddParent,
    onAddStudent,
    triggerPaymentStateSyncMutation,
    triggerScheduledDropsMutation,
    triggerScheduledInvoicesMutation,
    triggerUpcomingPaymentsMutation,
    triggerMembershipFeeReminderMutation,
    triggerExpiredEventsMutation,
  ]);
};
